import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { mergeClasses, Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle } from '@fluentui/react-components';
import { ShellIcons } from './shell/icons.js';
import { usePopupShape } from './popups.js';
/**
 * The one dialog every pop-up is built on (Figma `1038:2` cards 5 to 12; round 2's 14, 15 and 17; #501, #502).
 *
 * What it holds, so no screen has to remember it:
 * - a size (480, 560 or up to 960), never taller than 90% of the window, the middle scrolling with the title and buttons in view;
 * - Cancel on the left, the one primary on the right; under 600px wide they stack with the action on top;
 * - where the keyboard starts, set in an effect that runs AFTER Fluent's own focusing (Fluent's effect is in `Dialog`, a child
 *   of this component, and a parent's effect runs after its children's), so there is no timer to lose a race under load;
 * - X, Esc and a click outside do what Cancel does; while working none of them do anything;
 * - while working the primary cannot run its action again, whether it is an `onClick` or a submit button for a form (`form=`);
 * - when something was typed, all of them ask "Discard what you typed?" inside this same dialog, Keep editing on the left
 *   with the keyboard, Discard on the right; when nothing was, they close it at once;
 * - when it closes, the keyboard goes back to the button that opened it.
 */
export function PopupDialog({ open, title, size = 'small', onClose, primary, cancelLabel = 'Cancel', cancelAriaLabel, startOn = 'first', busy = false, dirty = false, dismissible = true, showX = true, pinned, ariaLabel, opener, children, }) {
    const shape = usePopupShape(size);
    const [discarding, setDiscarding] = useState(false);
    const cancelRef = useRef(null);
    const primaryRef = useRef(null);
    const keepRef = useRef(null);
    const contentRef = useRef(null);
    const returnTo = useRef(null);
    // The opener is whatever has the keyboard at the moment this opens: a layout effect runs before Fluent moves it.
    useLayoutEffect(() => {
        if (open)
            returnTo.current = opener?.current ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    }, [open, opener]);
    // Where the keyboard starts. Runs after Fluent's own focusing; see the header.
    useEffect(() => {
        if (!open)
            return;
        if (startOn === 'cancel') {
            cancelRef.current?.focus();
            return;
        }
        // The first field, not the X: the X sits first in the page order, and Fluent focuses the first thing it finds.
        const field = startOn === 'first' ? contentRef.current?.querySelector('input:not([disabled]), select:not([disabled]), textarea:not([disabled])') : null;
        (field ?? primaryRef.current)?.focus();
    }, [open, startOn]);
    // Closed: give the keyboard back, and forget a half-asked discard question.
    //
    // DEFERRED ON PURPOSE, unlike the start focus. Fluent's modalizer (Tabster) is still active for a moment after `open` turns false,
    // and focusing an element outside it in that moment left the page behind aria-hidden: a roster of status messages and buttons
    // that a role query could not find. Seen in loaded runs of `tests/offer-stage-section.test.tsx` (the sent window of Send onboarding items,
    // "keeps the welcome-email warning..." and "lets HR dismiss the warning"), which fails with the immediate focus and passes with this one-tick
    // deferral: Review 8 ran 11 offer test files together under 12 busy-loop processes (load 35) on #525, 3 of 3 failed with the immediate
    // focus and 3 of 3 passed with the timer. Without that load it did not fail (several quiet runs). So it is caught by that test under load,
    // and by no test of its own; a quiet run proves nothing about it. A screen reader would have met the same hidden page.
    const wasOpen = useRef(false);
    useEffect(() => {
        if (open) {
            wasOpen.current = true;
            return;
        }
        setDiscarding(false);
        if (wasOpen.current) {
            wasOpen.current = false;
            const back = returnTo.current;
            setTimeout(() => back?.focus(), 0);
        }
    }, [open]);
    useEffect(() => { if (discarding)
        keepRef.current?.focus(); }, [discarding]);
    const requestClose = () => {
        if (busy || !dismissible && !discarding)
            return;
        if (discarding) {
            setDiscarding(false);
            return;
        }
        if (dirty) {
            setDiscarding(true);
            return;
        }
        onClose();
    };
    /**
     * Cancel is a way of closing like the others, so it asks too. It has NO busy guard of its own: while working the button is
     * `disabledFocusable`, so a click never reaches this (pinned by "while it is working nothing closes it" in
     * `tests/popup-dialog.test.tsx`, which fails when that prop goes). `requestClose` guards the X, Esc and click outside.
     */
    const cancel = () => { if (dirty)
        setDiscarding(true);
    else
        onClose(); };
    const busyNow = busy && !discarding;
    /**
     * The working primary is `disabledFocusable`: marked off for a screen reader and still able to hold the keyboard. Fluent then gives
     * the button no `onClick`, and that is all that stops an `onClick` primary. It does not stop a SUBMIT button: the browser sends the
     * form for a click on a button that is only aria-disabled. A cancelled click sends nothing, so the click is cancelled here. That
     * covers the mouse, Enter or Space on the button, and Enter in a field of its form (when this is the form's submit button, the
     * browser sends the form by clicking it). Pinned by "one press starts it, and three more…" in `tests/popup-dialog.test.tsx`; the
     * keys are pressed in a real browser in `e2e/roster-popups.spec.ts`.
     */
    const swallow = (event) => { event.preventDefault(); };
    return (_jsx(Dialog, { open: open, modalType: dismissible ? 'modal' : 'alert', onOpenChange: (_, data) => { if (!data.open)
            requestClose(); }, children: _jsx(DialogSurface, { className: shape.surface, "aria-label": ariaLabel, children: _jsxs(DialogBody, { className: shape.body, children: [_jsx(DialogTitle, { action: dismissible && showX && !busy ? _jsx(Button, { appearance: "subtle", "aria-label": "Close", icon: _jsx(ShellIcons.close, {}), onClick: requestClose }) : undefined, children: discarding ? 'Discard what you typed?' : title }), _jsx(DialogContent, { ref: contentRef, className: shape.content, style: discarding ? { display: 'none' } : undefined, children: children }), discarding ? _jsx(DialogContent, { className: shape.content, children: "What you typed in this form will be lost." }) : null, discarding || pinned === undefined ? null : _jsx("div", { className: shape.pinned, children: pinned }), _jsx(DialogActions, { className: discarding || pinned === undefined ? shape.actions : mergeClasses(shape.actions, shape.belowPinned), children: discarding ? (_jsxs(_Fragment, { children: [_jsx(Button, { ref: keepRef, appearance: "secondary", className: shape.button, onClick: () => setDiscarding(false), children: "Keep editing" }), _jsx(Button, { appearance: "primary", className: shape.button, onClick: () => { setDiscarding(false); onClose(); }, children: "Discard" })] })) : (_jsxs(_Fragment, { children: [cancelLabel === null ? null : (_jsx(Button, { ref: cancelRef, appearance: "secondary", className: shape.button, "aria-label": cancelAriaLabel, disabledFocusable: busyNow, onClick: cancel, children: cancelLabel })), _jsx(Button, { ref: primaryRef, appearance: "primary", className: shape.button, type: primary.type ?? 'button', form: primary.form, disabled: primary.disabled === true && !busyNow, disabledFocusable: busyNow, onClickCapture: busyNow ? swallow : undefined, onClick: primary.onClick, children: busyNow ? (primary.busyLabel ?? primary.label) : primary.label })] })) })] }) }) }));
}
//# sourceMappingURL=PopupDialog.js.map